import React from 'react';
import {
  Box,
  Divider,
  Typography,
  FormHelperText,
  Checkbox,
  ListSubheader,
  TextField,
  Chip,
  MenuItem,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';

const RegionCountriesSelector = ({ regionList, formik }) => {
  const allCountryCodes = regionList.map((region) => region.countryCode);

  const areAllSelected = allCountryCodes.every((code) =>
    formik.values.regionCountries?.includes(code)
  );

  const isSelectAllIndeterminate =
    formik.values.regionCountries?.length > 0 && !areAllSelected;

  const handleSelectAll = () => {
    if (areAllSelected) {
      // Deselect all
      formik.setFieldValue('regionCountries', []);
    } else {
      // Select all
      formik.setFieldValue('regionCountries', allCountryCodes);
    }
  };

  const handleRegionSelection = (region) => {
    const regionOptions = options.filter((option) => option.group === region);
    const isRegionSelected = regionOptions.every((option) =>
      formik.values.regionCountries?.includes(option.value)
    );

    if (isRegionSelected) {
      // Deselect the entire region
      formik.setFieldValue(
        'regionCountries',
        formik.values.regionCountries.filter(
          (code) => !regionOptions.some((option) => option.value === code)
        )
      );
    } else {
      // Select the entire region
      formik.setFieldValue(
        'regionCountries',
        [
          ...formik.values?.regionCountries,
          ...regionOptions
            .filter((option) => !formik.values?.regionCountries.includes(option.value))
            .map((option) => option.value),
        ]
      );
    }
  };

  // Dynamically create options with "Select All" or "Unselect All"
  const options = [
    {
      group: 'Special',
      label: areAllSelected ? 'Unselect All' : 'Select All', // Update label dynamically
      value: 'select_all',
    },
    ...regionList
      .filter(region => region.regionName)
      .map((region) => ({
        group: region.regionName || 'No Region', // Use "No Region" for null regionName
        label: region.countryLongName,
        value: region.countryCode,
      }))
      .sort((a, b) => a.group.localeCompare(b.group)), // Sort by region (group)
  ];

  return (
    <Box sx={{ mt: 2, mb: 0 }}>
      <Divider textAlign="left">Developed in Country or Region:</Divider>
      <Typography variant="p" sx={{ color: '#464646', fontSize: 15 }}>
        Please select all regions or countries where users can purchase this product.
        <br />
        <em>*Multiple choices can be applied</em>
      </Typography>
      <Autocomplete
        multiple
        id="regionCountries"
        options={options}
        groupBy={(option) => (option.group === 'Special' ? '' : option.group)} // Group by region
        getOptionLabel={(option) => option.label} // Label for dropdown
        value={[
          ...options.filter((option) =>
            formik.values.regionCountries?.includes(option.value)
          ),
        ]} // Map selected country codes to their corresponding objects
        onChange={(event, newValue) => {
          if (newValue.find((option) => option.value === 'select_all')) {
            // Handle "Select All" or "Unselect All" option
            handleSelectAll();
          } else {
            // Update Formik field on selection change
            formik.setFieldValue(
              'regionCountries',
              newValue.map((option) => option.value) // Store only countryCode(s) in Formik
            );
          }
        }}
        disableCloseOnSelect // Keep dropdown open after selecting an option
        renderInput={(params) => (
          <TextField
            {...params}
            label="Destination Country or Region*"
            variant="outlined"
            sx={{ minWidth: 220, mt: 3 }}
            error={formik.touched.regionCountries && Boolean(formik.errors.regionCountries)}
          />
        )}
        renderTags={(value, getTagProps) => {
          const displayedTags = value.slice(0, 3); // Show up to 3 selected options
          const remainingCount = value.length - displayedTags.length;

          return [
            ...displayedTags.map((option, index) => (
              <Chip key={option.value} label={option.label} {...getTagProps({ index })} />
            )),
            remainingCount > 0 && (
              <Chip key="more" label={`+${remainingCount} more`} disabled />
            ),
          ];
        }}
        renderGroup={(params) => {
          const { key, group, children } = params;

          const regionOptions = options.filter((option) => option.group === group);
          const isRegionSelected =
            group &&
            regionOptions.every((option) =>
              formik.values.regionCountries?.includes(option.value)
            );
          const isRegionIndeterminate =
            group &&
            regionOptions.some((option) =>
              formik.values.regionCountries?.includes(option.value)
            ) && !isRegionSelected;

          return (
            <div key={key}>
              {group && (
                <ListSubheader
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <Checkbox
                    checked={isRegionSelected}
                    indeterminate={isRegionIndeterminate}
                    onClick={() => handleRegionSelection(group)}
                  />
                  <Typography variant="subtitle1" component="span" sx={{ fontWeight: 'bold' }}>
                    {group}
                  </Typography>
                </ListSubheader>
              )}
              {children}
            </div>
          );
        }}
        renderOption={(props, option, { selected }) => (
          <MenuItem
            {...props}
            key={option.value}
            sx={{
              pl: option.group && option.group !== 'Special' ? '32px !important' : '16px', // More specific padding
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Checkbox
              checked={
                option.value === 'select_all' ? areAllSelected : selected
              }
            />
            {option.label}
          </MenuItem>
        )}
      />
      <FormHelperText className="error-message">
        {formik.touched.regionCountries && formik.errors.regionCountries
          ? formik.errors.regionCountries
          : ' '}
      </FormHelperText>
    </Box>
  );
};

export default RegionCountriesSelector;
